import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, FormInstance, Table, message } from "antd";
import React, { RefObject } from "react";
import { RouteComponentProps } from "react-router";
import { NavLink } from "react-router-dom";
import Card from "../components/Card";
import EntitledSection from "../components/EntitledSection";
import entitlements from "../config/entitlements";
import { APIError, Department, FormFieldConfig } from "../helpers/shared-interfaces";
import { handleValidationErrors, request, showTotalPagination } from "../helpers/utilities";
import { CREATE_DEPARTMENT, GET_DEPARTMENTS_WITH_USERS } from "../gql/departments";
import ModalCreator from "../components/ModalCreator";
import BaseForm from "../components/Form/BaseForm";
import { AxiosResponse } from "axios";
import * as Sentry from "@sentry/browser";
import { PageToolbar } from "../components/PageToolbar";
import { generateDisplayErrorMessage } from "../helpers/utilities";


interface State {
  isFiltering: boolean;
  departments: Department[];
  filteredDepartments: Department[];
  filterBy: string;
  isLoading: boolean;
  showDialog: boolean;
  showNewDepartmentModal: boolean;
  isCreatingDepartment: boolean;
}

export function UserCountRender(text: string, record: Department): JSX.Element {
  return <span>{record.users.length}</span>;
}

export const columns = [
  {
    title: "Department Name",
    dataIndex: "name",
    key: "Department Name",
    width: "65%",
    render(text: string, record: Department): JSX.Element {
      return (
        <NavLink
          to={`/departments/${record.id}`}
          data-cy="test-department-name-link"
        >
          {text}
        </NavLink>
      );
    },
  },
  {
    title: "Number of Users",
    dataIndex: "users",
    key: "Number of Users",
    render: UserCountRender,
    width: "20%",
  },
  {
    title: "Action",
    dataIndex: "action",
    key: "action",
    align: "center" as const,
    render(text: string, record: Department): JSX.Element {
      return (
        <span>
          <NavLink to={`/departments/${record.id}/edit?name=${record.name}`}>
            <EditOutlined />
          </NavLink>
        </span>
      );
    },
  },
];

export function DepartmentTableFactory(data: Department[], isLoading: boolean): JSX.Element {
  const tempData: Department[] = data.map(
    (department: Department): Department => {
      return { ...department, key: department.id };
    },
  );
  return (
    <Table
      loading={isLoading}
      scroll={{ x: 500 }}
      columns={columns}
      dataSource={tempData}
      pagination={{
        pageSize: 10,
        showTotal: showTotalPagination,
      }}
    />
  );
}

export const NewDepartmentUI = (props: {
  reference: React.RefObject<FormInstance<any>>;
}): JSX.Element => {
  const fields: FormFieldConfig[] = [
    {
      name: "name",
      label: "Name",
      inputType: "string",
      placeholder: "Enter Department Name",
      rule: "string",
      required: true,
      colConfig: {
        span: 22,
      },
    },
  ];

  return (
    <BaseForm
      layout="vertical"
      reference={props.reference}
      fieldsToHide={[]}
      fields={fields}
    />
  );
};

export default class AllDepartments extends React.Component<
  RouteComponentProps,
  State
> {
  reference: RefObject<FormInstance>;

  constructor(props: RouteComponentProps) {
    super(props);
    this.state = {
      showDialog: false,
      filterBy: "",
      isLoading: true,
      departments: [],
      filteredDepartments: [],
      isFiltering: false,
      showNewDepartmentModal: false,
      isCreatingDepartment: false
    };
    this.reference = React.createRef<FormInstance>();
    this.toggleDeleteConfirmationModal =
      this.toggleDeleteConfirmationModal.bind(this);
    this.toggleCreateDepartmentModal =
      this.toggleCreateDepartmentModal.bind(this);
    this.getAllDepartments = this.getAllDepartments.bind(this);
    this.filterDepartments = this.filterDepartments.bind(this);
  }

  toggleDeleteConfirmationModal(): void {
    this.setState((prevState) => ({
      showDialog: !prevState.showDialog
    }));
  }

  async filterDepartments(
    e: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> {
    e.persist();
    this.setState({ filterBy: e.target.value, isFiltering: true });
    const filterBy: string = e.target.value;
    if (filterBy.length === 0) {
      this.setState({ isFiltering: false });
      return;
    }
    const filtered: Department[] = this.state.departments.filter(function (
      deparment: Department,
    ): boolean {
      if (deparment.name.toLowerCase().includes(filterBy.toLowerCase())) {
        return true;
      }
      return false;
    });
    this.setState({ filteredDepartments: filtered });
  }

  getAllDepartments = async (): Promise<void> => {
    this.setState({ isLoading: true });
    try {
      const { data: { data } }: any = await request({
        query: GET_DEPARTMENTS_WITH_USERS,
      });
      this.setState({
        departments: data.departments,
      });
    } catch (error) {
      message.error(`Failed to load departments - ${generateDisplayErrorMessage(error)}`);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  createDepartment = async (): Promise<void> => {
    interface Response {
      data?: {
        createDepartment?: {
          department?: {
            name: string;
          };
          errors?: APIError[];
        };
      };
      errors?: Record<string, unknown>[];
    }

    if (!this.reference.current) return;

    let data;

    this.setState({ isCreatingDepartment: true });

    try {
      data = await this.reference.current.validateFields(["name"]);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    try {
      const response: AxiosResponse<Response> = await request({
        query: CREATE_DEPARTMENT,
        variables: data,
      });
      if (
        response &&
        response.data &&
        response.data.data &&
        response.data.data.createDepartment &&
        response.data.data.createDepartment.department
      ) {
        message.success("Department created sucessfully");
        this.getAllDepartments();
      } else {
        message.error("Failed to create department");
      }
    } catch (error) {
      message.error(`Failed to create department - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isCreatingDepartment: false });
      this.toggleCreateDepartmentModal();
    }
  };

  toggleCreateDepartmentModal(): void {
    this.setState((prevState) => ({
      showNewDepartmentModal: !prevState.showNewDepartmentModal
    }));
  }

  async componentDidMount(): Promise<void> {
    await this.getAllDepartments();
  }

  render(): JSX.Element | null {
    const { departments, isFiltering, filteredDepartments, isLoading, showNewDepartmentModal } =
      this.state;
    return (
      <Card>
        <div className="deparments-table-container">
          <PageToolbar modelName="departments"
            searchValue={this.state.filterBy}
            onSearchChange={this.filterDepartments}
            searchPlaceholder="Filter by name..."
            primaryAction={
              <EntitledSection entitlement={entitlements.Departments.create}>
                <Button
                  type="primary"
                  onClick={this.toggleCreateDepartmentModal}
                  data-cy="test-new-department-button"
                >
                  <PlusOutlined /> Create Department
                </Button>
              </EntitledSection>
            }
            totalCount={departments.length}
            currentDataCount={isFiltering ? filteredDepartments.length : departments.length}
            page={1}
            pageSize={departments.length}
          />
          {DepartmentTableFactory(isFiltering ? filteredDepartments : departments, isLoading)}
          {showNewDepartmentModal &&
            ModalCreator({
              title: "Create Department",
              okType: "primary",
              okText: "Create",
              showModal: showNewDepartmentModal,
              onOkay: this.createDepartment,
              toggleModal: this.toggleCreateDepartmentModal,
              inscription: NewDepartmentUI({
                reference: this.reference,
              }),
            })}
        </div>
      </Card>
    );
  }
}
